/**
 * SSO config dialog — used to create a fresh per-org SSO config or edit an
 * existing one. Distinguishes the two modes via the optional `existing` prop.
 *
 * On create, the dialog collects the provider + OAuth credentials + matcher;
 * the server encrypts the client secret before persisting. On edit, the same
 * dialog can rotate the secret, change the matcher, or toggle enabled/auto-provision.
 *
 * The dialog never displays plaintext secrets retrieved from the server —
 * only an indicator that one is set. Rotating requires entering a new one.
 */
import { useState, type FormEvent } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog.js';
import { Button } from '@/components/ui/Button.js';
import { Input } from '@/components/ui/Input.js';
import { Label } from '@/components/ui/Label.js';
import { api, ApiError, type OrgSsoConfig, type SsoProvider } from '@/api/client.js';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: string;
  orgName: string;
  orgSlug: string;
  /** Pass an existing row to edit; omit for create. */
  existing?: OrgSsoConfig | null;
  onSaved: () => void;
}

export function SsoConfigDialog({ open, onOpenChange, orgId, orgName, orgSlug, existing, onSaved }: Props): JSX.Element {
  const isEdit = !!existing;
  const [provider, setProvider] = useState<SsoProvider>(existing?.provider ?? 'github');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [workspaceDomain, setWorkspaceDomain] = useState(
    existing?.matcher.workspaceDomain ?? '',
  );
  const [organizationLogin, setOrganizationLogin] = useState(
    existing?.matcher.organizationLogin ?? '',
  );
  const [defaultRole, setDefaultRole] = useState<'viewer' | 'admin'>(
    existing?.defaultGrantedPermissions.includes('core.org.admin') ? 'admin' : 'viewer',
  );
  const [autoProvision, setAutoProvision] = useState(existing?.autoProvision ?? true);
  const [enabled, setEnabled] = useState(existing?.enabled ?? true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Provider can't be changed in edit mode — it's the unique key alongside orgId.
  const effectiveProvider: SsoProvider = isEdit ? existing!.provider : provider;
  const matcherFieldFilled =
    effectiveProvider === 'google' ? workspaceDomain.trim().length > 0 : organizationLogin.trim().length > 0;

  function reset() {
    setProvider(existing?.provider ?? 'github');
    setClientId('');
    setClientSecret('');
    setWorkspaceDomain(existing?.matcher.workspaceDomain ?? '');
    setOrganizationLogin(existing?.matcher.organizationLogin ?? '');
    setDefaultRole(existing?.defaultGrantedPermissions.includes('core.org.admin') ? 'admin' : 'viewer');
    setAutoProvision(existing?.autoProvision ?? true);
    setEnabled(existing?.enabled ?? true);
    setError(null);
    setSubmitting(false);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (!matcherFieldFilled) {
      setError(
        effectiveProvider === 'google'
          ? 'Workspace domain is required (e.g. acme.com)'
          : 'GitHub organization login is required',
      );
      return;
    }

    setSubmitting(true);
    try {
      const matcher =
        effectiveProvider === 'google'
          ? { workspaceDomain: workspaceDomain.trim() }
          : { organizationLogin: organizationLogin.trim() };
      const defaultGrantedPermissions =
        defaultRole === 'admin' ? ['core.org.admin'] : ['core.org.read'];

      if (isEdit && existing) {
        await api.organisations.updateSsoConfig(orgId, existing.id, {
          ...(clientId ? { clientId } : {}),
          ...(clientSecret ? { clientSecret } : {}),
          matcher,
          defaultGrantedPermissions,
          autoProvision,
          enabled,
        });
        toast.success('SSO config updated');
      } else {
        if (!clientId || !clientSecret) {
          setError('Client ID and Client Secret are required to create a new SSO config.');
          setSubmitting(false);
          return;
        }
        await api.organisations.createSsoConfig(orgId, {
          provider: effectiveProvider,
          clientId,
          clientSecret,
          matcher,
          defaultGrantedPermissions,
          autoProvision,
          enabled,
        });
        toast.success(`${effectiveProvider} SSO configured for ${orgName}`);
      }
      onSaved();
      onOpenChange(false);
      reset();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save SSO config');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit SSO config' : 'Configure SSO'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? `Update ${existing!.provider} SSO for ${orgName}. Leave the client ID + secret fields empty to keep them unchanged.`
              : `Wire an OAuth identity provider to ${orgName}. Anyone matching the matcher can sign in (and, if auto-provisioning is on, get an account automatically).`}
          </DialogDescription>
        </DialogHeader>

        <form className="space-y-4" onSubmit={submit}>
          {!isEdit && (
            <div className="space-y-1.5">
              <Label>Provider</Label>
              <div className="flex gap-2">
                {(['github', 'google'] as SsoProvider[]).map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setProvider(p)}
                    className={
                      'flex-1 rounded-md border px-3 py-2 text-sm transition-colors ' +
                      (provider === p
                        ? 'border-primary bg-primary/10 text-foreground'
                        : 'border-border bg-background text-muted-foreground hover:bg-accent/40')
                    }
                  >
                    {p === 'google' ? 'Google Workspace' : 'GitHub'}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="cid">Client ID {isEdit && <span className="text-xs text-muted-foreground">(leave blank to keep)</span>}</Label>
            <Input
              id="cid"
              required={!isEdit}
              value={clientId}
              placeholder={isEdit ? `…${existing!.clientIdLast4}` : effectiveProvider === 'google' ? '123456789-abcdef.apps.googleusercontent.com' : 'Iv1.xxxxxxxx'}
              onChange={(e) => setClientId(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cs">Client Secret {isEdit && <span className="text-xs text-muted-foreground">(leave blank to keep)</span>}</Label>
            <Input
              id="cs"
              type="password"
              required={!isEdit}
              value={clientSecret}
              placeholder={isEdit ? '••••••' : 'paste from your provider'}
              onChange={(e) => setClientSecret(e.target.value)}
              autoComplete="off"
            />
            <p className="text-xs text-muted-foreground">Encrypted at rest. Never returned by the API after save.</p>
          </div>

          {effectiveProvider === 'google' ? (
            <div className="space-y-1.5">
              <Label htmlFor="wd">Workspace domain</Label>
              <Input
                id="wd"
                value={workspaceDomain}
                onChange={(e) => setWorkspaceDomain(e.target.value)}
                placeholder="acme.com"
              />
              <p className="text-xs text-muted-foreground">
                Users with a Google account in this Workspace domain will be matched on sign-in.
              </p>
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label htmlFor="ol">GitHub organisation</Label>
              <Input
                id="ol"
                value={organizationLogin}
                onChange={(e) => setOrganizationLogin(e.target.value)}
                placeholder="heydon-org"
              />
              <p className="text-xs text-muted-foreground">
                The lowercase login of the GitHub org. Users signing in must be members of it.
              </p>
            </div>
          )}

          <div className="space-y-1.5">
            <Label>Default role for auto-provisioned users</Label>
            <div className="flex gap-2">
              {(['viewer', 'admin'] as const).map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setDefaultRole(r)}
                  className={
                    'flex-1 rounded-md border px-3 py-2 text-sm capitalize transition-colors ' +
                    (defaultRole === r
                      ? 'border-primary bg-primary/10 text-foreground'
                      : 'border-border bg-background text-muted-foreground hover:bg-accent/40')
                  }
                >
                  {r}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-2 rounded-md border border-border bg-background/60 p-3">
            <ToggleRow label="Enabled" hint="Off = the start URL refuses." value={enabled} onChange={setEnabled} />
            <ToggleRow
              label="Auto-provision"
              hint="When on, never-seen users from the matcher get an account on first sign-in. Off = SSO works only for users who already have a DeployIt account."
              value={autoProvision}
              onChange={setAutoProvision}
            />
          </div>

          {error && (
            <p className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              {error}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : isEdit ? 'Save changes' : 'Create config'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ToggleRow({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint: string;
  value: boolean;
  onChange: (v: boolean) => void;
}): JSX.Element {
  return (
    <label className="flex cursor-pointer items-start gap-3">
      <input
        type="checkbox"
        checked={value}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1"
      />
      <div className="flex-1">
        <div className="text-sm font-medium">{label}</div>
        <div className="text-xs text-muted-foreground">{hint}</div>
      </div>
    </label>
  );
}
